<script setup lang="ts">
import { ref, computed, watch } from "vue";
import { UiButton, UiDialog, UiSelect, UiText, useToaster } from "@shopexperts/shared";
import type { UseMutationReturnType } from "@tanstack/vue-query";
import type { QuotePaymentStatus, RawQuoteRecord } from "@/v2Projects/types";
import { formatCurrency } from "@/v2Projects/utils/display";
import {
  formatQuotePaymentStatusLabel,
  formatQuoteStatusLabel,
  getQuotePaymentBadgeClass,
  getQuoteStatusBadgeClass,
  normalizeQuotePaymentStatus,
  quotePaymentStatusOptions,
} from "@/v2Projects/utils/quotePresentation";
import { reportProjectUserError } from "@/v2Projects/userErrors";

type UpdateQuotePaymentStatusMutation = UseMutationReturnType<
  unknown,
  Error,
  { quoteId: number; actingOrgId: number; paymentStatus: QuotePaymentStatus },
  unknown
>;

const props = defineProps<{
  quote: RawQuoteRecord;
  actingOrgId: number;
  updateQuotePaymentStatus: UpdateQuotePaymentStatusMutation;
}>();

const open = defineModel<boolean>({ default: false });
const toaster = useToaster();

const isSubmitting = ref(false);
const selectedPaymentStatus = ref<QuotePaymentStatus>("unpaid");

const currentPaymentStatus = computed(() =>
  normalizeQuotePaymentStatus(props.quote.payment_status),
);

watch(
  () => [open.value, props.quote.id, props.quote.payment_status] as const,
  ([isOpen]) => {
    if (!isOpen) return;
    selectedPaymentStatus.value = currentPaymentStatus.value;
  },
  { immediate: true },
);

async function handleSubmit() {
  if (isSubmitting.value) return;
  if (selectedPaymentStatus.value === currentPaymentStatus.value) {
    open.value = false;
    return;
  }

  isSubmitting.value = true;

  try {
    await props.updateQuotePaymentStatus.mutateAsync({
      quoteId: props.quote.id,
      actingOrgId: props.actingOrgId,
      paymentStatus: selectedPaymentStatus.value,
    });
    toaster.create({ title: "Payment status updated", type: "success" });
    open.value = false;
  } catch (error) {
    const message = reportProjectUserError("updateQuotePaymentStatus", error, {
      extra: {
        quoteId: props.quote.id,
        actingOrgId: props.actingOrgId,
        paymentStatus: selectedPaymentStatus.value,
      },
    });
    toaster.create({ title: message.title, description: message.description, type: "error" });
  } finally {
    isSubmitting.value = false;
  }
}
</script>

<template>
  <UiDialog
    v-model="open"
    size="sm"
    title="Update Payment Status"
    @close="open = false"
  >
    <div class="space-y-5">
      <div class="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-4">
        <div class="flex items-start justify-between gap-4">
          <div>
            <UiText as="p" variant="caption-normal" class="uppercase tracking-[0.18em] text-gray-500">
              Quote amount
            </UiText>
            <UiText as="p" variant="p" class="mt-1 font-semibold text-gray-900">
              {{ formatCurrency(quote.amount_cents, quote.currency) }}
            </UiText>
          </div>

          <div class="flex flex-wrap justify-end gap-1.5">
            <span
              class="inline-flex items-center rounded-sm border px-2 py-0.5 text-[11px] font-semibold"
              :class="getQuoteStatusBadgeClass(quote.status)"
            >
              {{ formatQuoteStatusLabel(quote.status) }}
            </span>
            <span
              class="inline-flex items-center rounded-sm border px-2 py-0.5 text-[11px] font-semibold"
              :class="getQuotePaymentBadgeClass(currentPaymentStatus)"
            >
              {{ formatQuotePaymentStatusLabel(currentPaymentStatus) }}
            </span>
          </div>
        </div>
      </div>

      <div>
        <UiText as="label" variant="p-small" class="mb-2 block font-medium text-gray-700">
          Payment status
        </UiText>
        <UiSelect
          v-model="selectedPaymentStatus"
          :options="quotePaymentStatusOptions"
        />
        <UiText as="p" variant="p-small" class="mt-2 text-gray-500">
          Use this when the client has paid externally or when you need to correct the payment state.
        </UiText>
      </div>
    </div>

    <template #footer>
      <div class="mt-6 flex items-center justify-end gap-3 border-t border-gray-200 pt-4">
        <UiButton
          variant="outline"
          label="Cancel"
          :disabled="isSubmitting"
          @click="open = false"
        />
        <UiButton
          variant="primary"
          :label="selectedPaymentStatus === currentPaymentStatus ? 'Close' : 'Update Payment Status'"
          :disabled="isSubmitting"
          @click="handleSubmit"
        />
      </div>
    </template>
  </UiDialog>
</template>
